import moment from "moment";
import React, { useMemo } from "react";

import Box from "veneer/Box";
import Chart, { ChartOptions } from "veneer/Chart";
import { Colors } from "veneer/Theme";
import Typography from "veneer/Typography";

import { utcToLocal } from "cortex/ui/src/util/time";
import { Panel } from "cortex/ui/src/view/layout";

export type ConnectionChartPoint = readonly [number, boolean];

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;

interface ConnectionChartProps {
  data: ReadonlyArray<ConnectionChartPoint>;
  small?: boolean;
}

interface ChartPoint {
  color: string;
  name: "Online" | "Offline";
  timestamp: number;
}

function formatTickLabel(
  timestamp: number,
  previousTimestamp?: number,
): string {
  const current = moment.utc(timestamp).local();

  if (previousTimestamp === undefined) {
    return current.format("HH:mm");
  }

  const previous = moment.utc(previousTimestamp).local();
  if (current.isSame(previous, "day")) {
    return current.format("HH:mm");
  }

  return current.format("D. MMM");
}

export default function ConnectionChart({
  data,
  small = false,
}: ConnectionChartProps) {
  const noData = data.length === 0;

  const chartData = useMemo<ChartPoint[]>(() => {
    return data.map(([timestamp, status]) => ({
      color: status ? Colors.green700 : Colors.red700,
      name: status ? "Online" : "Offline",
      timestamp,
    }));
  }, [data]);

  const xBounds = useMemo(() => {
    if (chartData.length === 0) {
      return undefined;
    }

    const initialTimestamp = chartData[0].timestamp;
    const bounds = chartData.reduce(
      (acc, point) => ({
        max: Math.max(acc.max, point.timestamp),
        min: Math.min(acc.min, point.timestamp),
      }),
      {
        max: initialTimestamp,
        min: initialTimestamp,
      },
    );

    return {
      max: Math.ceil(bounds.max / TWO_HOURS_MS) * TWO_HOURS_MS,
      min: Math.floor(bounds.min / TWO_HOURS_MS) * TWO_HOURS_MS,
    };
  }, [chartData]);

  const chartOptions = useMemo<ChartOptions<"bar">>(() => {
    return {
      animation: false,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: Colors.white,
          borderColor: chartData[0]?.color ?? Colors.gray300,
          borderWidth: 1,
          bodyColor: Colors.gray700,
          callbacks: {
            label: (item) => {
              const point = chartData[item.dataIndex];
              return point ? utcToLocal(point.timestamp) : "";
            },
            title: (items) => {
              const point = chartData[items[0]?.dataIndex ?? -1];
              return point?.name ?? "";
            },
          },
          displayColors: false,
          padding: 12,
          titleColor: Colors.gray1000,
        },
      },
      scales: {
        x: {
          type: "linear",
          bounds: "data",
          min: xBounds?.min,
          max: xBounds?.max,
          border: {
            color: Colors.gray300,
            display: !small,
          },
          grid: {
            display: false,
          },
          ticks: {
            autoSkip: true,
            stepSize: TWO_HOURS_MS,
            callback: (value, index, ticks) => {
              const previousValue = ticks[index - 1]?.value;
              const previousTimestamp =
                previousValue == null ? undefined : Number(previousValue);

              return formatTickLabel(Number(value), previousTimestamp);
            },
            display: !small,
            maxRotation: 0,
            minRotation: 0,
          },
        },
        y: {
          beginAtZero: true,
          border: {
            display: false,
          },
          display: false,
          grid: {
            display: false,
          },
          max: 1,
          min: 0,
        },
      },
    };
  }, [chartData, small, xBounds]);

  const chart = (
    <Box dataTestId="connection-chart">
      <Chart
        data={{
          datasets: [
            {
              backgroundColor: chartData.map((point) => point.color),
              barThickness: 2,
              borderRadius: 0,
              borderWidth: 0,
              categoryPercentage: 1,
              data: chartData.map((point) => ({
                x: point.timestamp,
                y: 1,
              })) as any,
              hoverBackgroundColor: chartData.map((point) => point.color),
              label: "Connection status",
              maxBarThickness: 3,
            },
          ],
        }}
        height={small ? 60 : 200}
        options={chartOptions}
        type="bar"
      />
    </Box>
  );

  if (noData) {
    const emptyState = (
      <Box dataTestId="connection-chart-empty">
        <Typography>No data for chart.</Typography>
      </Box>
    );

    if (small) {
      return emptyState;
    }

    return <Panel title="Connection Status">{emptyState}</Panel>;
  }

  if (small) {
    return chart;
  }

  return <Panel title="Connection Status">{chart}</Panel>;
}
